import React, { useEffect, useState } from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { api, apiError } from "@/lib/apiClient";
import { GripVertical, Route as RouteIcon, Save, Loader2, MapPin, Clock, Sparkles, Check, X } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/statuts";
import { DatePicker } from "@/components/DatePicker";

const numIcon = (n) => L.divIcon({
  className: "",
  html: `<div style="background:#2563eb;color:#fff;width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 2px 5px rgba(0,0,0,.3);font-size:12px;font-weight:700">${n}</div>`,
  iconSize: [26, 26], iconAnchor: [13, 13],
});

const homeIcon = L.divIcon({
  className: "",
  html: `<div style="background:#059669;color:#fff;width:28px;height:28px;border-radius:8px;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 2px 5px rgba(0,0,0,.3);font-size:14px">⌂</div>`,
  iconSize: [28, 28], iconAnchor: [14, 14],
});

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) map.setView(points[0], 13);
    else if (points.length > 1) map.fitBounds(points, { padding: [40, 40] });
  }, [points, map]);
  return null;
}

function SortableStop({ v, index }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: v.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} data-testid={`stop-${v.id}`} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3">
      <button {...attributes} {...listeners} className="cursor-grab text-slate-300 hover:text-slate-500"><GripVertical className="h-5 w-5" /></button>
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#0A0A0A] text-xs font-bold text-white">{index + 1}</span>
      <div className="flex-1">
        <div className="text-sm font-semibold text-slate-900">{v.heure_debut || ""} · {v.client_nom}</div>
        <div className="flex items-center gap-1 text-xs text-slate-400"><MapPin className="h-3 w-3" />{v.adresse}, {v.code_postal} {v.ville}</div>
      </div>
      {v.distance_precedent_km != null && (
        <div className="text-right text-xs">
          <div className="mono font-bold text-blue-600">{v.distance_precedent_km} km</div>
          <div className="flex items-center gap-0.5 text-slate-400"><Clock className="h-3 w-3" />~{v.temps_precedent_min} min</div>
        </div>
      )}
    </div>
  );
}

export default function Tournee() {
  const [techs, setTechs] = useState([]);
  const [tech, setTech] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [stops, setStops] = useState([]);
  const [summary, setSummary] = useState({ distance_totale: 0, temps_total: 0 });
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [home, setHome] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => { api.get("/users/techniciens").then((r) => setTechs(r.data)).catch(() => {}); }, []);

  const homeFor = (techId) => {
    const t = techs.find((x) => x.id === techId);
    if (t && t.latitude != null && t.longitude != null) {
      return { latitude: t.latitude, longitude: t.longitude, label: `Domicile ${t.prenom} ${t.nom}`, ville: t.ville, code_postal: t.code_postal };
    }
    return null;
  };

  const build = async () => {
    if (!tech) { toast.error("Sélectionnez un technicien"); return; }
    setLoading(true);
    setSuggestion(null);
    const h = homeFor(tech);
    setHome(h);
    try {
      const { data } = await api.get(`/visites?technicien_id=${tech}&date_visite_from=${date}&date_visite_to=${date}&limit=100`);
      // Ordre enregistré (source de vérité côté technicien) s'il existe
      let saved = [];
      try {
        const { data: trs } = await api.get(`/tournees?technicien_id=${tech}&date=${date}`);
        if (trs && trs.length && Array.isArray(trs[0].visite_ids)) saved = trs[0].visite_ids;
      } catch { /* pas de tournée enregistrée */ }
      const byId = new Map(data.items.map((v) => [v.id, v]));
      let ordered;
      if (saved.length) {
        // On respecte l'ordre enregistré, puis on ajoute les nouveaux RDV en fin de liste
        const inSaved = saved.map((id) => byId.get(id)).filter(Boolean);
        const seen = new Set(saved);
        const extra = data.items
          .filter((v) => !seen.has(v.id))
          .sort((a, b) => (a.heure_debut || "99:99").localeCompare(b.heure_debut || "99:99"));
        ordered = [...inSaved, ...extra];
      } else {
        ordered = data.items.slice().sort((a, b) => (a.heure_debut || "99:99").localeCompare(b.heure_debut || "99:99"));
      }
      const ids = ordered.map((v) => v.id);
      if (ids.length === 0) { setStops([]); setSummary({ distance_totale: 0, temps_total: 0 }); toast.info("Aucune visite ce jour-là"); setLoading(false); return; }
      const { data: opt } = await api.post("/planning/distance", { visite_ids: ids, respect_order: true, start: h, end: h });
      setStops(opt.ordre);
      setSummary({ distance_totale: opt.distance_totale, temps_total: opt.temps_total, retour: opt.retour });
      if (saved.length) toast.info("Ordre enregistré chargé");
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const recalc = async (newStops) => {
    const h = homeFor(tech);
    try {
      const { data } = await api.post("/planning/distance", { visite_ids: newStops.map((s) => s.id), respect_order: true, start: h, end: h });
      setStops(data.ordre);
      setSummary({ distance_totale: data.distance_totale, temps_total: data.temps_total, retour: data.retour });
    } catch { setStops(newStops); }
  };

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (over && active.id !== over.id) {
      const oldI = stops.findIndex((s) => s.id === active.id);
      const newI = stops.findIndex((s) => s.id === over.id);
      const moved = arrayMove(stops, oldI, newI);
      setStops(moved);
      recalc(moved);
    }
  };

  const suggest = async () => {
    try {
      const { data } = await api.get(`/planning/optimiser?technicien_id=${tech}&date=${date}`);
      setSuggestion(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const applySuggestion = async () => {
    const ordered = suggestion.suggeree.map((s) => s.id);
    setSuggestion(null);
    await recalc(ordered.map((id) => ({ id })));
    toast.success("Optimisation appliquée");
  };

  const saveTournee = async () => {
    try {
      await api.post("/tournees", { technicien_id: tech, date, visite_ids: stops.map((s) => s.id), nom: `Tournée ${formatDate(date)}` });
      toast.success("Tournée enregistrée");
    } catch (e) { toast.error(apiError(e)); }
  };

  const mapStops = stops.filter((s) => s.latitude != null);
  const hasHome = home && home.latitude != null;
  const linePoints = [
    ...(hasHome ? [[home.latitude, home.longitude]] : []),
    ...mapStops.map((s) => [s.latitude, s.longitude]),
    ...(hasHome ? [[home.latitude, home.longitude]] : []),
  ];
  const center = mapStops.length ? [mapStops[0].latitude, mapStops[0].longitude] : (hasHome ? [home.latitude, home.longitude] : [45.75, 4.85]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">Tournée</h1>
        <p className="text-sm text-slate-500">Sélectionnez une date et un technicien, visualisez la tournée sur la carte et optimisez l'ordre.</p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4">
        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Technicien</label>
          <select data-testid="tournee-tech" value={tech} onChange={(e) => setTech(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
            <option value="">Sélectionner…</option>
            {techs.map((t) => <option key={t.id} value={t.id}>{t.prenom} {t.nom}</option>)}
          </select>
        </div>
        <div className="w-44">
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Date</label>
          <DatePicker value={date} onChange={(v) => setDate(v)} testid="tournee-date" clearable={false} />
        </div>
        <button data-testid="tournee-build" onClick={build} disabled={loading} className="flex items-center gap-2 rounded-lg bg-[#0A0A0A] px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RouteIcon className="h-4 w-4" />} Construire la tournée
        </button>
        {stops.length > 1 && (
          <button data-testid="tournee-suggest" onClick={suggest} className="flex items-center gap-2 rounded-lg border border-violet-300 bg-violet-50 px-4 py-2 text-sm font-semibold text-violet-700 hover:bg-violet-100">
            <Sparkles className="h-4 w-4" /> Suggérer une optimisation
          </button>
        )}
        {stops.length > 0 && (
          <button data-testid="tournee-save" onClick={saveTournee} className="flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold hover:bg-slate-50">
            <Save className="h-4 w-4" /> Enregistrer
          </button>
        )}
      </div>

      {stops.length > 0 && (
        <>
          <div className="flex flex-wrap gap-4 rounded-xl border border-blue-200 bg-blue-50 p-4">
            <div><div className="text-[11px] uppercase text-blue-500">Arrêts</div><div className="font-heading text-2xl font-black text-blue-700">{stops.length}</div></div>
            <div><div className="text-[11px] uppercase text-blue-500">Distance totale</div><div className="font-heading text-2xl font-black text-blue-700">{summary.distance_totale} km</div></div>
            <div><div className="text-[11px] uppercase text-blue-500">Temps trajet</div><div className="font-heading text-2xl font-black text-blue-700">~{summary.temps_total} min</div></div>
            {hasHome ? (
              <div><div className="text-[11px] uppercase text-emerald-600">Départ / Retour domicile</div><div className="font-heading text-sm font-bold text-emerald-700">{home.code_postal} {home.ville}{summary.retour ? ` · retour ${summary.retour.distance_km} km` : ""}</div></div>
            ) : (
              <div><div className="text-[11px] uppercase text-amber-600">Domicile technicien</div><div className="text-xs text-amber-600">Non renseigné (ajoutez-le dans Utilisateurs)</div></div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={stops.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {stops.map((v, i) => <SortableStop key={v.id} v={v} index={i} />)}
                </div>
              </SortableContext>
            </DndContext>

            <div className="overflow-hidden rounded-xl border border-slate-200" style={{ height: "460px" }} data-testid="tournee-map">
              <MapContainer center={center} zoom={11} style={{ height: "100%", width: "100%" }}>
                <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <FitBounds points={linePoints.length ? linePoints : mapStops.map((s) => [s.latitude, s.longitude])} />
                <Polyline positions={linePoints} pathOptions={{ color: "#2563eb", weight: 3, dashArray: "6 8" }} />
                {hasHome && (
                  <Marker position={[home.latitude, home.longitude]} icon={homeIcon}>
                    <Popup><b>Domicile</b><br />{home.code_postal} {home.ville}</Popup>
                  </Marker>
                )}
                {mapStops.map((s, i) => (
                  <Marker key={s.id} position={[s.latitude, s.longitude]} icon={numIcon(i + 1)}>
                    <Popup><b>{i + 1}. {s.client_nom}</b><br />{s.heure_debut} · {s.code_postal} {s.ville}</Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>
        </>
      )}

      {suggestion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSuggestion(null)} />
          <div className="relative z-10 w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl" data-testid="suggestion-modal">
            <div className="mb-4 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-violet-600" />
              <h2 className="font-heading text-lg font-bold">Suggestion d'optimisation</h2>
            </div>
            <div className="mb-4 flex gap-4 rounded-lg bg-slate-50 p-3 text-sm">
              <div>Actuelle : <b>{suggestion.distance_actuelle} km</b></div>
              <div>Suggérée : <b className="text-emerald-600">{suggestion.distance_suggeree} km</b></div>
              <div>Gain : <b className={suggestion.gain_km > 0 ? "text-emerald-600" : "text-slate-500"}>{suggestion.gain_km} km · ~{suggestion.temps_gagne_min} min</b></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">Ordre actuel</div>
                <ol className="space-y-1 text-sm">
                  {suggestion.actuelle.map((s, i) => <li key={s.id} className="rounded bg-slate-50 px-2 py-1">{i + 1}. {s.heure} — {s.ville}</li>)}
                </ol>
              </div>
              <div>
                <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-violet-500">Ordre suggéré</div>
                <ol className="space-y-1 text-sm">
                  {suggestion.suggeree.map((s, i) => <li key={s.id} className="rounded bg-violet-50 px-2 py-1">{i + 1}. {s.heure} — {s.ville}</li>)}
                </ol>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button data-testid="suggestion-cancel" onClick={() => setSuggestion(null)} className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold hover:bg-slate-50"><X className="h-4 w-4" /> Annuler</button>
              <button data-testid="suggestion-apply" onClick={applySuggestion} className="flex items-center gap-1.5 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700"><Check className="h-4 w-4" /> Appliquer la suggestion</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
